import { useMemo, useCallback } from 'react';

import { Link } from '@mui/material';
import { Column } from 'react-table';
import { useQueryParams } from 'use-query-params';

import { fetchUsers } from 'api';
import { Table } from 'components/Table';
import { colors } from 'config';
import { getPageCount } from 'helpers';
import { StringParam, BooleanParam } from 'helpers/useQueryParams';
import { useUrlPagination, useControlledPagination } from 'hooks';
import { UserProps } from 'types';

import { DashboardWrapper } from '../styledComponents';
import { UserTableSidebar } from './UserTableSidebar';

const filterConfig = {
  organization_name: StringParam,
  email: StringParam,
  first_name: StringParam,
  last_name: StringParam,
  last_login_min: StringParam,
  last_login_max: StringParam,
  is_cognito: BooleanParam,
};

export const AuditorUserTable = () => {
  const { controlledRows, controlledPageIndex } = useUrlPagination();
  const [filters, setFilters] = useQueryParams(filterConfig, {
    updateType: 'replaceIn',
  });
  const config = useMemo(() => {
    const {
      organization_name,
      email,
      first_name,
      last_name,
      last_login_max,
      last_login_min,
      is_cognito,
    } = filters;
    return {
      params: {
        page_number: controlledPageIndex,
        limit: controlledRows,
        first_name: first_name,
        last_name: last_name,
        organization_name: organization_name,
        email: email,
        is_cognito: is_cognito,
        last_login_min: last_login_min,
        last_login_max: last_login_max,
        role: 'employer',
        fuzzy: true,
      },
    };
  }, [controlledPageIndex, controlledRows, filters]);
  const { dataArray, dataLoaded, count } = useControlledPagination(
    fetchUsers,
    config,
  );
  const { updateControlledPageIndex } = useUrlPagination();

  // update and return to first page when filter is applied
  const wrapSetCandidateFilter = useCallback(
    (arg: Parameters<typeof setFilters>[0]) => {
      setFilters(arg);
      updateControlledPageIndex(1);
    },
    [setFilters, updateControlledPageIndex],
  );
  const columns = useMemo(
    (): Array<Column<UserProps>> => [
      {
        id: 'first_name',
        Header: 'First Name',
        accessor: 'first_name',
        disableFilters: true,
        disableSortBy: true,
      },
      {
        id: 'last_name',
        Header: 'Last Name',
        accessor: 'last_name',
        disableFilters: true,
        disableSortBy: true,
      },
      {
        id: 'email',
        Header: 'Email',
        accessor: 'email',
        disableFilters: true,
        disableSortBy: true,
      },
      {
        id: 'organization',
        Header: 'Employers',
        accessor: (row) => {
          const { organization } = row;

          return organization ? (
            <Link href={`employers/${organization.id}`}>
              {organization.name}
            </Link>
          ) : (
            ''
          );
        },
        disableFilters: true,
        disableSortBy: true,
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: 'account_status',
        disableFilters: true,
        disableSortBy: true,
      },
    ],
    [],
  );
  return (
    <DashboardWrapper>
      <>
        <UserTableSidebar
          setFilters={wrapSetCandidateFilter}
          filters={filters}
        />
        <Table<UserProps>
          count={count}
          loading={!dataLoaded}
          rowProps={(row) => {
            return !row.is_cognito
              ? {
                  style: {
                    backgroundColor: colors.uswds.orange10,
                    borderBottom: '1px solid white',
                  },
                }
              : {};
          }}
          useTableOptions={{
            data: dataArray,
            columns: columns,
            manualPagination: true,
            pageCount: getPageCount(count, controlledRows),
            autoResetPage: false,

            // defaultCanSort: false,
            // defaultCanFilter: false,
            // canFilter: false,
            // canSort:false,
            disableSort: true,
          }}
        />
      </>
    </DashboardWrapper>
  );
};
